import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

import { CostCenter } from '../models/cost-center.model';
import { MiscUtil } from '../utilities/misc.util';
import { ShiftSchedule } from '../models/shift-schedule.model';
import { ShiftScheduleService } from './shift-schedule.service';
import { StaffingGridType } from 'src/app/smt/staffing/staffing-grid/utilities/staffing-grid-constants.util';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { ShiftRecord } from '../models/shift-record.model';
import { CostCenterService } from './cost-center.service';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class CostCenterConfigurationService {
    staffingGridLockOutMessage: string;
    constructor(private _shiftScheduleService: ShiftScheduleService,private _costCenterService: CostCenterService) { }

    public warningMessage: string;
    public errorMessage: string;
    public isSaving: boolean;
    public isComplete = false;
    public futureShiftRecord : ShiftRecord;

    private gridTypeButtonSource = new Subject<StaffingGridType>();
    public gridButtonPressed$ = this.gridTypeButtonSource.asObservable();

    private reloadAdminListSource = new Subject<boolean>();
    public reloadAdminList$ = this.reloadAdminListSource.asObservable();
  
    private selectedSource = new Subject<CostCenter>();
    public selected$ = this.selectedSource.asObservable();
    
    private saveSource = new Subject<string>();
    public save$ = this.saveSource.asObservable();

    private warningSource = new Subject<string>();
    public warning$ = this.warningSource.asObservable();

    private errorSource = new Subject<string>();
    public error$ = this.errorSource.asObservable();

    private isFormValidSource = new Subject<boolean>();
    public isFormValid$ = this.isFormValidSource.asObservable();

    private isFormDirtySource = new Subject<boolean>();
    public isFormDirty$ = this.isFormDirtySource.asObservable();

    private validatingSource = new Subject<boolean>();
    public isValidating$ = this.validatingSource.asObservable();
    
    private isFutureShiftRecordPresentSource = new Subject<boolean>();
    public isFutureShiftRecordPresent$ = this.isFutureShiftRecordPresentSource.asObservable();
    
    private tab = 'general';
    
    private adminActionLoadingRequests = 0;
    private adminListLoadingRequests = 0;

    get isAdminActionLoading() : boolean {
        return this.adminActionLoadingRequests > 0;
    }

    get isAdminListLoading(): boolean {
        return this.adminListLoadingRequests > 0;
    }

    get generalTabActive() : boolean  {
        return this.tab === 'general';
    }
  
    get detailsTabActive() : boolean {
        return this.tab === 'details';
    }

    get staffingTabActive() : boolean {
        return this.tab === 'staffingGrid';
    }

    setActiveTab(tab: string) {
        this.tab = tab;
    }

    setSelected(costCenter: CostCenter) {
        this.selectedSource.next(costCenter);
    }    

    announceCompleteStaffingGrid(isComplete: boolean) {
        this.isComplete = isComplete as boolean;
    }

    announceCostCenterSaveError(costCenter: CostCenter) {
        let errorMessage = "Cost center failed to save.";

        if (costCenter.errorMessage) {
            errorMessage = costCenter.errorMessage;
            costCenter.errorMessage = null;
        }

        this.announceError(errorMessage);
    }
  
    announceError(errorMessage?: string) {
        this.errorSource.next(errorMessage || "");
    }

    announceGridTypeButtonPressed(gridType: StaffingGridType) {
        this.gridTypeButtonSource.next(gridType);
    }
    
    clearError() {
        this.errorSource.next("");
    }

    announceWarning(warning?: string) {
        this.warningSource.next(warning || "");
    }

    clearWarning() {
        this.warningSource.next("");
    }
  
    announceSaving(saving: boolean) {
        this.isSaving = saving;
    }

    announceAdminActionLoading(loading: boolean) {
        if (loading) {
            this.adminActionLoadingRequests++;      
        } 
        else if (this.adminActionLoadingRequests > 0) {
            this.adminActionLoadingRequests--;
        }
    }

    announceAdminListLoading(loading: boolean) {
        if (loading) {  
            this.adminListLoadingRequests++;
        }
        else if (this.adminListLoadingRequests > 0) {
            this.adminListLoadingRequests--;
        }
    }

    announceSave() {
        this.announceSaving(true);
        this.saveSource.next(this.tab);
    }

    announceFormIsValid(isFormValid = true){
        this.isFormValidSource.next(isFormValid);
    }

    announceFormIsDirty(isFormDirty = true){
        this.isFormDirtySource.next(isFormDirty);
    }
    
    announceAdminListReload(mostRecent: boolean) {
        this.reloadAdminListSource.next(mostRecent);
    }

    announceValidating(isValidating = true) {
        this.validatingSource.next(isValidating);
    }

    private enableSaveOnDetailsTab(costCenter: CostCenter): boolean {
        let isAcuity = WorkflowPageUtil.hasAcuityConfiguration(costCenter) || WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter);
        let isInpatient = WorkflowPageUtil.hasInpatientConfiguration(costCenter);
        let isUos = WorkflowPageUtil.hasUosConfiguration(costCenter);

        if (!this.enableSaveOnGeneralTab(costCenter)) {
            return false;
        }

        if (isInpatient) {
            return this.meetsInpatientRequirements(costCenter);
        }
        else if (isAcuity || isUos) {
            return this.meetsAncillaryRequirements(costCenter);
        }
        
        return false;
    }

    private enableSaveOnGeneralTab(costCenter: CostCenter): boolean {
        return costCenter && 
            costCenter.facility && 
            costCenter.name && 
            MiscUtil.checkArray(costCenter.jobTitles) &&                 
            MiscUtil.checkArray(costCenter.varianceReasons);
    }

    enableSaveOnStaffingGrid(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        //When a cost center is active or if its not inpatient then ensure that staffing grids are
        //completely filled out.  Inactive inpatient grids are allowed to have partial grids filled out.
        if (!costCenter.inactiveDate || !WorkflowPageUtil.hasInpatientConfiguration(costCenter)) {
            return this._shiftScheduleService.checkSchedules(costCenter, schedules);
        }
        
       return this.enableSaveOnDetailsTab(costCenter) && schedules.some(s => MiscUtil.isValidNonNegativeNumber(s.startHour));
    }

    private meetsAcuityRequirements(costCenter: CostCenter): boolean {
        if (!WorkflowPageUtil.hasAcuityConfiguration(costCenter) && !WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter)) {
            return false;
        }

        let configuration = costCenter.ancillaryConfiguration;

        if (!MiscUtil.checkArray(configuration.acuityCategories)) {
            return false;
        }

        if (WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter) && 
            !configuration.roomTypes.some(r => MiscUtil.checkArray(r.rooms))) {
            return false;
        }

        return MiscUtil.isValidNonNegativeNumber(configuration.targetWHPUOS);     
    }

    private meetsAncillaryRequirements(costCenter: CostCenter): boolean {
        if (!costCenter || !costCenter.ancillaryConfiguration) {
            return false;
        }        

        let usesAcuity = WorkflowPageUtil.hasAcuityConfiguration(costCenter) ||
            WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter);

        if (usesAcuity && !this.meetsAcuityRequirements(costCenter)) {
            return false;
        }

        if (WorkflowPageUtil.hasUosConfiguration(costCenter) && !this.meetsUosRequirements(costCenter)) {            
            return false;
        }

        let configuration = costCenter.ancillaryConfiguration;

        return !!configuration.typeOfWorkTemplate &&
            MiscUtil.isValidPositiveNumber(configuration.unitOfServiceId);
    } 

    private meetsInpatientRequirements(costCenter: CostCenter): boolean {
        if (!costCenter || !costCenter.inpatientConfiguration) {
            return false;
        }

        let configuration = costCenter.inpatientConfiguration;

        if (!configuration.totalPatientCensus && !MiscUtil.checkArray(configuration.patientRatios)) {
            return false;
        }      

        return MiscUtil.isValidNonNegativeNumber(configuration.targetWHPUOS) &&
            MiscUtil.isValidNonNegativeNumber(configuration.minCensus) &&
            MiscUtil.isValidNonNegativeNumber(configuration.maxCensus) &&
            configuration.maxCensus > configuration.minCensus;
    }

    private meetsUosRequirements(costCenter: CostCenter): boolean {
        if (!WorkflowPageUtil.hasUosConfiguration(costCenter)) {
            return false;
        }       

        let configuration = costCenter.ancillaryConfiguration;        

        return MiscUtil.isValidNonNegativeNumber(configuration.fixedTargetWHPUOS) &&
            MiscUtil.isValidNonNegativeNumber(configuration.variableTargetWHPUOS);
    }  
    checkAndAnnounceFutureShiftRecordPresence(costCenterId: number) {
        if (costCenterId > 0) {
            this._costCenterService.getFutureShiftRecordForCostCenter(costCenterId).then(shiftRecord => {
                this.futureShiftRecord = shiftRecord;
                if (shiftRecord) {
                    let shiftRecordDate = moment(shiftRecord.workflowTimeFacility).format('MM/DD/YYYY h:mm A');
                    this.staffingGridLockOutMessage = `Future staffing plan data locks staffing grid until ${shiftRecordDate}`
                }
                else {
                    this.staffingGridLockOutMessage = '';
                }
                this.isFutureShiftRecordPresentSource.next(this.staffingGridLockOutMessage != '');
            });
        }
        else {
            this.staffingGridLockOutMessage = '';
            this.isFutureShiftRecordPresentSource.next(false);
        }
    }
}
